//
// Copyright 2024 DXOS.org
//

// @import-as-namespace

import * as Schema from 'effect/Schema';

import { DXN } from '@dxos/keys';

import * as Annotation from './Annotation.ts';
import * as internal from './internal/index.ts';
import * as Obj from './Obj.ts';
import * as Ref from './Ref.ts';
import * as Type from './Type.ts';

/**
 * {@link Annotation.UserType} tag for types made to live in a collection, which creating into a collection
 * offers. Any user type can join a collection; untagged ones do so from the object itself.
 */
export const ItemTag = 'org.dxos.tag.collectionItem';

/**
 * A an ordered set of objects.
 */
export class Collection extends Type.makeObject<Collection>(DXN.make('org.dxos.type.collection', '0.1.0'))(
  Schema.Struct({
    name: Schema.String.pipe(Schema.optional),
    /** Members, in order. */
    objects: Schema.Array(Ref.Ref(Obj.Unknown)).pipe(
      Annotation.SetParent.set({ override: false }),
      internal.FormInputAnnotation.set(false),
    ),
  }).pipe(
    Annotation.IconAnnotation.set({ icon: 'ph--folder--regular', hue: 'indigo' }),
    Annotation.UserType.set({ tags: [ItemTag] }),
  ),
) {}

/**
 * Creates a new collection object.
 *
 * @performance O(n) in initial members; allocates an in-memory collection object.
 */
export const make = (props: Partial<Obj.MakeProps<typeof Collection>> = {}): Type.InstanceType<typeof Collection> =>
  Obj.make(Collection, { objects: [], ...props });

/**
 * Type guard for collections.
 *
 * @performance O(1) type-URI comparison with a typename fallback; no schema validation.
 */
export const isCollection: (value: unknown) => value is Type.InstanceType<typeof Collection> =
  Obj.instanceOf(Collection);
